import { createFileRoute } from '@tanstack/react-router';
import {
  Calculator,
  ChevronRight,
  Dumbbell,
  Home,
  Layers,
  LineChart,
  PawPrint,
  Scan,
  Sprout,
  Stethoscope,
  Waves,
  Wrench,
} from 'lucide-react';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/industries')({
  component: IndustriesPage,
});

interface Tile {
  Icon: typeof Stethoscope;
  title: string;
  summary: string;
  href: string;
}

interface TileGroup {
  eyebrow: string;
  heading: string;
  tiles: Tile[];
}

const GROUPS: TileGroup[] = [
  {
    eyebrow: 'Health & wellness',
    heading: 'Health & wellness',
    tiles: [
      {
        Icon: Scan,
        title: 'Medical imaging',
        summary:
          'Match each scan to the room and machine that can take it. No more bookings on the wrong modality.',
        href: '/for-medical-imaging',
      },
      {
        Icon: Stethoscope,
        title: 'Clinics & allied health',
        summary:
          'Practitioners, rooms and treatment spaces stay in sync. Long consults never get squeezed.',
        href: '/for-allied-health',
      },
      {
        Icon: Dumbbell,
        title: 'Personal trainers',
        summary:
          'Sessions at your studio, the client’s gym, or outdoors — with travel time built in.',
        href: '/for-personal-training',
      },
    ],
  },
  {
    eyebrow: 'Professional services',
    heading: 'Professional services',
    tiles: [
      {
        Icon: Calculator,
        title: 'Accountants & tax agents',
        summary:
          'BAS, tax-return and advisory meetings booked against the right specialist, with prep questions sent ahead.',
        href: '/for-accountants',
      },
      {
        Icon: Home,
        title: 'Mortgage brokers',
        summary:
          'First-meet, doc-collection and settlement bookings — with the documents you need attached to the right step.',
        href: '/for-mortgage-brokers',
      },
      {
        Icon: LineChart,
        title: 'Financial planners',
        summary:
          'Discovery, strategy and review meetings — long sessions are protected, FDS reviews get rebooked on time.',
        href: '/for-financial-planners',
      },
    ],
  },
  {
    eyebrow: 'Home & property',
    heading: 'Home & property',
    tiles: [
      {
        Icon: Wrench,
        title: 'Mobile trades',
        summary:
          'Sparkies, plumbers and handymen — travel-time-aware bookings so the day actually fits.',
        href: '/for-trades',
      },
      {
        Icon: Waves,
        title: 'Pool care',
        summary:
          'Regular service runs, one-off cleans and equipment checks on a tidy weekly route.',
        href: '/for-pool-care',
      },
      {
        Icon: Sprout,
        title: 'Gardeners & landscapers',
        summary:
          'Mowing rounds, hedge days and bigger landscaping jobs slotted by area and crew size.',
        href: '/for-gardening',
      },
    ],
  },
  {
    eyebrow: 'Other services',
    heading: 'Other services',
    tiles: [
      {
        Icon: PawPrint,
        title: 'Mobile dog groomers',
        summary:
          'Suburb-by-suburb rounds, the right van stocked, and reminders that cut no-shows.',
        href: '/for-mobile-dog-grooming',
      },
      {
        Icon: Wrench,
        title: 'Independent mechanics',
        summary:
          'Logbook services, brakes, tyres — the right hoist and a follow-up list for regulars.',
        href: '/for-mechanics',
      },
    ],
  },
];

function IndustriesPage() {
  usePageTitle('Industries');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-14 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            <Layers className="h-4 w-4" />
            Who Bookaburra fits
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Built for any service business that{' '}
            <span className="text-brand-sage">books appointments.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            Here&rsquo;s how it shapes up for some common ones. Don&rsquo;t see
            yours?{' '}
            <a href="/contact" className="text-brand-sage hover:underline">
              It probably still works.
            </a>
          </p>
        </div>
      </section>

      {GROUPS.map((group, gi) => (
        <Section key={group.heading} tone={gi % 2 === 0 ? 'shadow' : undefined}>
          <div className="max-w-3xl">
            <SectionEyebrow>{group.eyebrow}</SectionEyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              {group.heading}
            </h2>
          </div>
          <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8">
            {group.tiles.map((tile) => (
              <li key={tile.href}>
                <a
                  href={tile.href}
                  className="group flex h-full flex-col rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.08)] transition hover:border-brand-sage/40 hover:shadow-[0_30px_80px_-20px_rgba(19,33,36,0.14)]"
                >
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-sage/10 text-brand-sage">
                      <tile.Icon className="h-5 w-5" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-brand-sage/60 transition group-hover:translate-x-0.5 group-hover:text-brand-sage" />
                  </div>
                  <h3 className="text-xl font-bold tracking-tight text-brand-ink-soft">
                    {tile.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                    {tile.summary}
                  </p>
                </a>
              </li>
            ))}
          </ul>
        </Section>
      ))}

      {/* Don't see yours? */}
      <Section tone={GROUPS.length % 2 === 0 ? 'shadow' : undefined}>
        <div className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-8 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.08)] sm:p-12">
          <SectionEyebrow>Don&rsquo;t see yours?</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
            It probably still works.
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-brand-ink-soft/80">
            Bookaburra is built around the bits every service business shares
            — staff, rooms, equipment, travel, reminders. If yours isn&rsquo;t on
            this page, tell us about it and we&rsquo;ll show you how it maps.
          </p>
          <a
            href="/contact"
            className="mt-7 inline-flex items-center gap-1 text-base font-medium text-brand-sage hover:underline"
          >
            Tell us about your business &rarr;
          </a>
        </div>
      </Section>
    </>
  );
}
